import {
  Card,
  CardNoteSide,
  CardRating,
  LinguisticMeta,
  Translations,
  VisualMeta,
} from '@lingoping/core';

/**
 * The operations a device sends to the `sync` Edge Function (Docs/SYNC_CONTRACT.md, section 3),
 * exactly as they travel, and where the list of operations the server has not answered yet (the
 * "outbox") is kept on this device. StorageManager adds to the outbox; services/syncEngine.ts
 * sends it and removes what was answered.
 */

/** The text and picture of a card: what `card.content` may change. Null empties a field. */
export interface CardContentFields {
  lemma?: string | null;
  front_text?: string;
  back_text?: string | null;
  part_of_speech?: string | null;
  linguistic_meta?: LinguisticMeta | null;
  visual_meta?: VisualMeta | null;
  image_url?: string | null;
  example_target?: string | null;
  example_native?: string | null;
  phonetic_ipa?: string | null;
  audio_url?: string | null;
  content_status?: 'ready' | 'pending';
  /** Added to the card's translations (per language the new entry wins), never replacing them. */
  translations?: Translations;
  /** Which meaning of the word the card is ('' = the first), and its short German hint. */
  sense_key?: string;
  sense_hint?: string | null;
}

/** A card's place in "Up next to learn". */
export interface CardQueueFields {
  queued_at?: string | null;
  queue_priority?: 0 | 1;
  source_deck_id?: string | null;
}

/** The settings that belong to the account, not to this device. */
export interface SyncedSettings {
  native_language?: string;
  new_words_per_day?: number;
  session_interval_minutes?: number;
  cards_per_session?: number;
}

/** The ranges the server accepts for the numbers among the settings. */
export const SETTING_RANGES = {
  new_words_per_day: [1, 500],
  session_interval_minutes: [1, 1440],
  cards_per_session: [1, 50],
} as const;

export interface KnownWordInput {
  id: string;
  lemma: string;
  language: string;
  base_lemma: string | null;
  created_at: string;
}

export type SyncOpBody =
  | { type: 'card.add'; card: Record<string, unknown> & { id: string } }
  | { type: 'card.content'; card_id: string; content: CardContentFields }
  | { type: 'card.review'; card_id: string; rating: CardRating; reviewed_at: string }
  | { type: 'card.retire'; card_id: string; retired_at: string | null }
  | { type: 'card.note'; card_id: string; side: CardNoteSide; text: string }
  | ({ type: 'card.queue'; card_id: string } & CardQueueFields)
  | { type: 'card.delete'; card_id: string }
  | { type: 'known.add'; words: KnownWordInput[] }
  | { type: 'known.remove'; word_id: string }
  | { type: 'known.remove_group'; base_lemma: string; language: string }
  | ({ type: 'settings.set'; onboarding_completed?: true } & SyncedSettings);

/** An operation in the outbox: `id` is a uuid made on the device, `at` the device's time. */
export type SyncOp = SyncOpBody & { id: string; at: string };

export const MAX_KNOWN_WORDS_PER_OP = 500;

/** The card an operation is about, if it is about one. */
export function opCardId(op: SyncOpBody): string | null {
  if (op.type === 'card.add') return op.card.id;
  return 'card_id' in op ? op.card_id : null;
}

/** The keys under which one learner's outbox and the sync engine's notes are kept. */
export function syncKeys(userId: string) {
  return {
    outbox: `lingoping_sync_outbox_${userId}`,
    state: `lingoping_sync_state_${userId}`,
    deviceId: `lingoping_sync_device_${userId}`,
    movedOver: `lingoping_cloud_first_${userId}`,
  };
}

/** True for the simulated development account: it has no server, so it never syncs. */
export function isDevMockUserId(userId: string | null | undefined): boolean {
  return !!userId && userId.startsWith('dev-');
}

/** A uuid, also where `crypto.randomUUID` is missing: the server refuses any other kind of id. */
export function newUuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** The fields of a card that `card.content` carries, as they are sent (missing = null). */
export function cardContentOf(card: Card): Required<CardContentFields> {
  return {
    lemma: card.lemma ?? null,
    front_text: card.front_text,
    back_text: card.back_text ?? null,
    part_of_speech: card.part_of_speech ?? null,
    linguistic_meta: card.linguistic_meta ?? null,
    visual_meta: card.visual_meta ?? null,
    image_url: card.image_url ?? null,
    example_target: card.example_target ?? null,
    example_native: card.example_native ?? null,
    phonetic_ipa: card.phonetic_ipa ?? card.linguistic_meta?.phonetic_ipa ?? null,
    audio_url: card.audio_url ?? null,
    content_status: card.content_status === 'pending' ? 'pending' : 'ready',
    translations: card.translations ?? {},
    sense_key: card.sense_key ?? '',
    sense_hint: card.sense_hint ?? null,
  };
}

/**
 * A new card as `card.add` carries it: its text, its place in "Up next", and whether it is
 * retired or has notes. The server always stores it as a card that was never reviewed.
 */
export function newCardOf(card: Card): Record<string, unknown> & { id: string } {
  return {
    id: card.id,
    target_language: card.target_language,
    ...cardContentOf(card),
    fsrs_state: card.fsrs_state,
    due_date: card.due_date,
    queued_at: card.queued_at ?? null,
    queue_priority: card.queue_priority === 1 ? 1 : 0,
    source_deck_id: card.source_deck_id ?? null,
    retired_at: card.retired_at ?? null,
    note_front: card.note_front ?? null,
    note_back: card.note_back ?? null,
    created_at: card.created_at,
  };
}
